import { api, download, request, setAccessToken, setStepUpToken } from './client';
import type {
  AccountPortfolio,
  AdminUser,
  ApprovalCount,
  ApprovalRequestItem,
  ControlsStatus,
  JobRunRow,
  AuditEntry,
  CurrentUser,
  DetailedHealth,
  Exchange,
  ExchangeAccount,
  LoginResult,
  MfaChallenge,
  RiskProfile,
  RiskProfileVersion,
  SignalRow,
  ControlRequestRow,
  ReportInfo,
  Strategy,
  StrategyAssignment,
  StrategyPlugin,
  TradeOrder,
} from './types';

export type LoginResponse = LoginResult | MfaChallenge;

export function isMfaChallenge(value: LoginResponse): value is MfaChallenge {
  return 'mfa_required' in value && value.mfa_required === true;
}

export const auth = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const result = await request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuthRetry: true,
    });
    if (!isMfaChallenge(result)) setAccessToken(result.access_token);
    return result;
  },

  async completeMfa(mfaToken: string, code: string): Promise<LoginResult> {
    const result = await request<LoginResult>('/auth/login/mfa', {
      method: 'POST',
      body: { mfa_token: mfaToken, code },
      skipAuthRetry: true,
    });
    setAccessToken(result.access_token);
    return result;
  },

  me: () => api.get<CurrentUser>('/auth/me'),

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setStepUpToken(null);
    }
  },

  async stepUp(password: string, code?: string): Promise<number> {
    const result = await request<{ step_up_token: string; expires_in: number }>('/auth/step-up', {
      method: 'POST',
      body: { password, code: code || null },
      skipAuthRetry: true,
    });
    setStepUpToken(result.step_up_token, result.expires_in);
    return result.expires_in;
  },

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ changed: boolean; sessions_revoked: number }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  enrollMfa: () => api.post<{ secret: string; provisioning_uri: string }>('/auth/mfa/enroll'),

  confirmMfa: (code: string) =>
    api.post<{ mfa_enabled: boolean; recovery_codes: string[] }>('/auth/mfa/confirm', { code }),
};

export const realtime = {
  ticket: () => api.post<{ ticket: string; expires_in: number }>('/ws/ticket'),
};

export const exchanges = {
  list: () => api.get<Exchange[]>('/exchanges'),
  accounts: () => api.get<ExchangeAccount[]>('/exchanges/accounts'),
  createAccount: (body: { exchange_code: string; label: string; mode: string }) =>
    api.post<ExchangeAccount>('/exchanges/accounts', body),
  enable: (id: number) => api.post<ExchangeAccount>(`/exchanges/accounts/${id}/enable`),
  disable: (id: number) => api.post<ExchangeAccount>(`/exchanges/accounts/${id}/disable`),
  /** Allow this account to place orders. Enabling alone does not: "enabled"
   *  means configured, "running" means it may trade. */
  start: (id: number) => api.post<ExchangeAccount>(`/exchanges/accounts/${id}/start`),
  /** Stop placing new orders. Positions and open orders are untouched. */
  pause: (id: number) => api.post<ExchangeAccount>(`/exchanges/accounts/${id}/pause`),
  putCredentials: (id: number, body: { api_key: string; api_secret: string; passphrase?: string }) =>
    api.put(`/exchanges/accounts/${id}/credentials`, body),
};

export const users = {
  list: () => api.get<AdminUser[]>('/users'),
  create: (body: { email: string; password: string; role: string }) =>
    api.post<AdminUser>('/users', body),
  update: (publicId: string, body: { role?: string; is_active?: boolean }) =>
    api.patch<AdminUser>(`/users/${publicId}`, body),
  unlock: (publicId: string) => api.post<AdminUser>(`/users/${publicId}/unlock`),
};

export const config = {
  riskProfiles: () => api.get<RiskProfile[]>('/risk-profiles'),
  riskVersions: (profileId: number) =>
    api.get<RiskProfileVersion[]>(`/risk-profiles/${profileId}/versions`),
  createRiskProfile: (body: { name: string; scope: string }) =>
    api.post<{ id: number; version: number }>('/risk-profiles', body),
  updateRiskLimits: (profileId: number, limits: Record<string, string>, changeNote: string) =>
    api.post<{ version: number; limits: Record<string, string>; relaxed_keys: string[] }>(
      `/risk-profiles/${profileId}/versions`,
      { limits, change_note: changeNote },
    ),
  strategies: () => api.get<Strategy[]>('/strategies'),
  strategyAssignments: () => api.get<StrategyAssignment[]>('/strategy-assignments'),
  assignStrategy: (body: {
    strategy_id: number;
    exchange_account_id: number;
    canonical_symbol?: string | null;
    capital_allocation_pct?: string | null;
  }) => api.post<StrategyAssignment>('/strategy-assignments', body),
  unassignStrategy: (id: number) =>
    api.delete<{ id: number; deleted: boolean }>(`/strategy-assignments/${id}`),
  createStrategy: (body: { name: string; plugin_key: string; description?: string }) =>
    api.post<{ id: number; version: number }>('/strategies', body),
  strategyPlugins: () => api.get<StrategyPlugin[]>('/strategy-plugins'),
  /** Enabling alone trades nothing: a strategy runs only where it is assigned. */
  enableStrategy: (id: number) => api.post<Strategy>(`/strategies/${id}/enable`),
  disableStrategy: (id: number) => api.post<Strategy>(`/strategies/${id}/disable`),
  /** Saved as a new version; open positions move to it at the next positions check. */
  changeTakeProfit: (id: number, takeProfitPct: string, changeNote: string) =>
    api.post<Strategy>(`/strategies/${id}/take-profit`, {
      take_profit_pct: takeProfitPct,
      change_note: changeNote,
    }),
};

export const health = {
  detailed: () => api.get<DetailedHealth>('/health/detailed'),
};

export const audit = {
  list: (limit = 50) => api.get<AuditEntry[]>(`/audit?limit=${limit}`),
  verify: () => api.get<{ intact: boolean; first_broken_row_id: number | null }>('/audit/verify'),
};

// ---------------------------------------------------------------- phase 9

export function fetchPortfolio(exchange?: string): Promise<AccountPortfolio[]> {
  const query = exchange ? `?exchange=${encodeURIComponent(exchange)}` : '';
  return request<AccountPortfolio[]>(`/portfolio${query}`);
}

export function fetchOrders(params: { open?: boolean; symbol?: string } = {}): Promise<TradeOrder[]> {
  const search = new URLSearchParams();
  if (params.open) search.set('open', 'true');
  if (params.symbol) search.set('symbol', params.symbol);
  const query = search.toString();
  return request<TradeOrder[]>(`/orders${query ? `?${query}` : ''}`);
}

export function cancelOrder(orderId: number): Promise<unknown> {
  return request(`/orders/${orderId}/cancel`, { method: 'POST' });
}

export function fetchApprovals(state = 'OPEN'): Promise<ApprovalRequestItem[]> {
  return request<ApprovalRequestItem[]>(`/approvals?state=${state}`);
}

/** Polled from the shell so an unread approval is visible on every screen. */
export function fetchApprovalCount(): Promise<ApprovalCount> {
  return request<ApprovalCount>('/approvals/count');
}

export function approveRequest(id: number, note: string): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>(`/approvals/${id}/approve`, {
    method: 'POST',
    body: { note },
  });
}

export function declineRequest(id: number, note: string): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>(`/approvals/${id}/decline`, {
    method: 'POST',
    body: { note },
  });
}

export function fetchControls(): Promise<ControlsStatus> {
  return request<ControlsStatus>('/controls');
}

export function emergencyStop(reason: string, scope = 'GLOBAL', scopeRef?: string): Promise<unknown> {
  return request('/controls/emergency-stop', {
    method: 'POST',
    body: { reason, scope, scope_ref: scopeRef },
  });
}

export function resumeTrading(reason: string, scope = 'GLOBAL', scopeRef?: string): Promise<unknown> {
  return request('/controls/resume', {
    method: 'POST',
    body: { reason, scope, scope_ref: scopeRef },
  });
}

export function fetchSignals(limit = 50): Promise<SignalRow[]> {
  return request<SignalRow[]>(`/signals?limit=${limit}`);
}

export function fetchJobRuns(limit = 50): Promise<JobRunRow[]> {
  return request<JobRunRow[]>(`/ops/jobs?limit=${limit}`);
}

/** Paper accounts start empty, so nothing trades until this is called. */
export function fundPaperAccount(
  accountId: number,
  amount: string,
  asset = 'USDT',
): Promise<{ account_id: number; asset: string; free: string }> {
  return request(`/exchanges/accounts/${accountId}/fund`, {
    method: 'POST',
    body: { amount, asset },
  });
}

// ------------------------------------------------ reports and operator controls

/** Admin only: the reports that can be exported, and the longest range. */
export function fetchReports(): Promise<{ reports: ReportInfo[]; max_days: number }> {
  return request('/reports');
}

/** A report as a CSV file, either the last `days` days or whole dates. */
export function downloadReport(
  key: string,
  range: { days: number } | { from: string; to: string },
): Promise<{ blob: Blob; filename: string }> {
  const query =
    'days' in range
      ? `days=${range.days}`
      : `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  return download(`/reports/${encodeURIComponent(key)}.csv?${query}`);
}

/**
 * Sell a whole position now: cancels the account's orders in the symbol and
 * sells no lower than 2% under the best bid (what the book cannot take in that
 * range stays held), through any kill switch and the loss-exit policy.
 * SUPER_ADMIN, step-up, and the symbol typed back as `confirm`.
 */
export function emergencyExit(body: {
  account_id: number;
  symbol: string;
  confirm: string;
  reason: string;
}): Promise<ControlRequestRow> {
  return request<ControlRequestRow>('/positions/emergency-exit', { method: 'POST', body });
}

export function fetchControlRequest(id: number): Promise<ControlRequestRow> {
  return request<ControlRequestRow>(`/control-requests/${id}`);
}

/** Checked by the scheduler, which alone may decrypt the key. */
export function verifyCredentials(accountId: number): Promise<ControlRequestRow> {
  return request<ControlRequestRow>(`/exchanges/accounts/${accountId}/verify-credentials`, {
    method: 'POST',
  });
}
